import _ from 'lodash';
import { LocalAtRecord, Mission, Sample, SamplingSpec, SoilCore } from '../db';
import { JobsFieldSet } from '@rogoag/airtable/crm';
import { getDuplicatesByKey, isNumeric } from '../utils';
import { getCurrentMission } from '../dataModelHelpers';

export type DumpResult<T = unknown> = { text: string; json: T };

const indentLines = (text: string, levels: number = 1): string => {
  const pad = '  '.repeat(levels);

  return text
    .split('\n')
    .map((line) => `${pad}${line}`)
    .join('\n');
};

export const printCore = (core: SoilCore | undefined): DumpResult => {
  if (!core) {
    console.log('Core is undefined');

    return { text: 'Core is undefined', json: { error: 'Core is undefined' } };
  }

  const lines: string[] = [];
  const header = `PRINT CORE ${core.name}${core.pulled ? ' pulled' : ''}`;

  console.group(`PRINT CORE ` + `${core.name}` + `${core.pulled ? ' %cpulled' : '%c'}`, 'color: green');
  lines.push(header);

  const log = (label: string, ...vals: unknown[]) => {
    console.log(label, ...vals);
    lines.push(`${label} ${vals.map((v) => String(v)).join(' ')}`);
  };

  log('core instance id', core.instance_id);
  log('core pulled at', core.pulled_at);
  log('core waypoint number', core.getCorePoint()?.waypoint_number);
  log('core waypoint index', core.waypoint_index);
  log('core lat and lon', core.lat, core.lon);
  log('core pulled lat and pulled lon', core.pulled_lat, core.pulled_lon);
  log('skipped or deleted', core.skipped_or_deleted);
  log('core length cm', core.core_length_cm);
  log('core dumped + guid', core.dumped, core.getSoilDumpEvent()?.guid);
  log('core in bucket ?', core.isInBucket());
  log('source', core.source);
  log('core_diameter_inches', core.core_diameter_inches);

  console.groupEnd();

  const json = {
    instance_id: core.instance_id,
    name: core.name,
    pulled: core.pulled,
    pulled_at: core.pulled_at,
    waypoint_number: core.getCorePoint()?.waypoint_number,
    waypoint_index: core.waypoint_index,
    lat: core.lat,
    lon: core.lon,
    pulled_lat: core.pulled_lat,
    pulled_lon: core.pulled_lon,
    skipped_or_deleted: core.skipped_or_deleted,
    core_length_cm: core.core_length_cm,
    dumped: core.dumped,
    soil_dump_event_guid: core.getSoilDumpEvent()?.guid,
    in_bucket: core.isInBucket(),
    source: core.source,
  };

  return { text: lines.join('\n'), json };
};

export const printSampleCores = (sample: Sample): DumpResult => {
  const sampleCores = sample.getSoilCores().sort((c1: SoilCore, c2: SoilCore) => {
    return c1.core_number - c2.core_number;
  });
  const pulledCores = sampleCores.filter((core: SoilCore) => core.pulled);

  const lines: string[] = [];
  const header = `PRINT SAMPLE ${sample.sample_id} - ${pulledCores.length} / ${sampleCores.length}`;

  console.group(header);
  lines.push(header);

  const log = (label: string, val: unknown) => {
    console.log(label, val);
    lines.push(`${label} ${String(val)}`);
  };

  log('sample', sample);
  log('sample site', sample.getSampleSite());
  log('sample spec', sample.getSamplingSpec());
  log('sample barcode', sample.bag_id);
  log('sample SampleBox_id', sample.SampleBox_id);
  log('sample box_uid', sample.box_uid);

  const coreJsons: unknown[] = [];

  sampleCores.forEach((core: SoilCore) => {
    const coreResult = printCore(core);
    lines.push(indentLines(coreResult.text, 1));
    coreJsons.push(coreResult.json);
  });

  console.groupEnd();

  const json = {
    sample_id: sample.sample_id,
    bag_id: sample.bag_id,
    SampleBox_id: sample.SampleBox_id,
    box_uid: sample.box_uid,
    pulled_count: pulledCores.length,
    total_count: sampleCores.length,
    cores: coreJsons,
  };

  return { text: lines.join('\n'), json };
};

export const printMissionSamplesInfo = (mission?: Mission, filterSampleId?: string) => {
  if (!mission) {
    mission = getCurrentMission();
  }

  if (!mission) {
    console.log('printMissionSamplesInfo - mission is undefined');

    return;
  }

  const missionSamples = mission
    .getSamples()
    .filter((sample: Sample) => !filterSampleId || sample.sample_id === filterSampleId)
    .sort((s1: Sample, s2: Sample) => {
      if (isNumeric(s1.sample_id) && isNumeric(s2.sample_id)) {
        return parseInt(s1.sample_id) - parseInt(s2.sample_id);
      }

      return s1.sample_id.localeCompare(s2.sample_id);
    });

  const orderedCores = mission.getAllCores().sort((c1: SoilCore, c2: SoilCore) => {
    return c1.core_number.toString().localeCompare(c2.core_number.toString());
  });

  const pulled = orderedCores.filter((core: SoilCore) => core.pulled);

  console.group(
    `PRINT MISSION SAMPLES INFO: ${mission.name} (inst ${mission.instance_id}) - cores pulled ${pulled.length} / ${orderedCores.length}`,
  );

  missionSamples.forEach((sample) => {
    console.log(
      `Sample ${sample.sample_id} (orig: ${sample.getOriginalSampleId()}) - cores pulled: ${sample.getPulledSoilCores().length} / ${sample.getSoilCores().length}, bag_id: ${sample.bag_id}, box_uid: ${sample.box_uid}`,
    );

    if (sample.change_type !== 'None' || sample.change_reason) {
      console.log(`change_type: ${sample.change_type}, change_reason: ${sample.change_reason}`);

      const backupHistoryParsed = JSON.parse(sample.backup_history) as [];

      if (backupHistoryParsed.length > 0) {
        console.log('backup_history parsed', backupHistoryParsed);
      }
    }
  });

  const duplicateSampleIds = mission.getDuplicateSamples();
  const duplicateSampleSites = mission.getDuplicateSampleSites();
  const samplesBelongingToOneSampleSite = getDuplicatesByKey(missionSamples, 'getOriginalSampleId');

  if (duplicateSampleIds.length) {
    console.log('%c duplicate sample ids', 'color: red', duplicateSampleIds);
  }

  if (duplicateSampleSites.length) {
    console.log('%c duplicate sample sites', 'color: red', duplicateSampleSites);
  }

  // Ok for Andes mission with more than one spec
  if (samplesBelongingToOneSampleSite.length) {
    console.log('%c samplesBelongingToOneSampleSite', 'color: orange', samplesBelongingToOneSampleSite);
  }

  console.groupEnd();
};

export const printMissionCores = (mission?: Mission, filterSampleId?: string): DumpResult => {
  if (!mission) {
    mission = getCurrentMission();
  }

  if (!mission) {
    const msg = 'printMissionCores - mission is undefined';
    console.log(msg);

    return { text: msg, json: { error: msg } };
  }

  const missionSamples = mission.getSamples().sort((s1: Sample, s2: Sample) => {
    if (isNumeric(s1.sample_id) && isNumeric(s2.sample_id)) {
      return parseInt(s1.sample_id) - parseInt(s2.sample_id);
    }

    return s1.sample_id.localeCompare(s2.sample_id);
  });

  const orderedCores = mission.getAllCores().sort((c1: SoilCore, c2: SoilCore) => {
    return c1.core_number.toString().localeCompare(c2.core_number.toString());
  });

  const pulled = orderedCores.filter((core: SoilCore) => core.pulled);

  const lines: string[] = [];
  const header = `PRINT MISSION CORES: ${mission.name} (inst ${mission.instance_id}) - ${pulled.length} / ${orderedCores.length}`;

  console.group(header);
  lines.push(header);

  const sampleJsons: unknown[] = [];

  missionSamples
    .filter((sample: Sample) => !filterSampleId || sample.sample_id === filterSampleId)
    .forEach((sample: Sample) => {
      const sampleResult = printSampleCores(sample);
      lines.push(indentLines(sampleResult.text, 1));
      sampleJsons.push(sampleResult.json);
    });

  let takenCoresParsed: unknown[] = [];
  let takenCoresError: string | undefined;

  try {
    takenCoresParsed = mission.cores ? JSON.parse(mission.cores) : [];
    console.log(`takenCores - ${takenCoresParsed.length}`, takenCoresParsed);
    lines.push(`takenCores - ${takenCoresParsed.length}`);
  } catch (e) {
    takenCoresError = String(e);
    console.log('error parsing mission.cores', e);
    lines.push(`error parsing mission.cores: ${takenCoresError}`);
  }

  console.groupEnd();

  const json = {
    mission_name: mission.name,
    mission_instance_id: mission.instance_id,
    pulled_count: pulled.length,
    total_count: orderedCores.length,
    samples: sampleJsons,
    taken_cores: takenCoresParsed,
    ...(takenCoresError ? { taken_cores_error: takenCoresError } : {}),
  };

  return { text: lines.join('\n'), json };
};

export const printMissionSpecs = (mission?: Mission | undefined): DumpResult => {
  if (!mission) {
    mission = getCurrentMission();
  }

  if (!mission) {
    const msg = 'printMissionSpecs - mission is undefined';
    console.log(msg);

    return { text: msg, json: { error: msg } };
  }

  const lines: string[] = [];
  const header = `PRINT MISSION SPECS: ${mission.name} (inst ${mission.instance_id})`;

  console.group(header);
  lines.push(header);

  const specs = mission.getSamplingSpecs();
  const specJsons = specs.map((spec: SamplingSpec) => {
    const specHeader = `Spec ${spec.spec_id}`;
    console.group(specHeader);

    const specLines: string[] = [specHeader];

    const log = (label: string, val: unknown) => {
      console.log(label, val);
      specLines.push(`${label} ${String(val)}`);
    };

    log('deal_id', spec.deal_id);
    log('primary_spec', spec.primary_spec);
    log('spec_id', spec.spec_id);
    log('lab_name', spec.lab_name);
    log('test_package', spec.test_package);
    log('cores', spec.cores);
    log('length', spec.length);
    log('parallel_line_distance_ft', spec.parallel_line_distance_ft);
    log('Mission_id', spec.Mission_id);

    console.groupEnd();
    lines.push(indentLines(specLines.join('\n'), 1));

    return {
      deal_id: spec.deal_id,
      primary_spec: spec.primary_spec,
      spec_id: spec.spec_id,
      lab_name: spec.lab_name,
      test_package: spec.test_package,
      cores: spec.cores,
      length: spec.length,
      parallel_line_distance_ft: spec.parallel_line_distance_ft,
      Mission_id: spec.Mission_id,
    };
  });

  console.groupEnd();

  const json = {
    mission_name: mission.name,
    mission_instance_id: mission.instance_id,
    specs: specJsons,
  };

  return { text: lines.join('\n'), json };
};

export const printMissionZones = (mission?: Mission | undefined): DumpResult => {
  if (!mission) {
    mission = getCurrentMission();
  }

  if (!mission) {
    const msg = 'printMissionZones - mission is undefined';
    console.log(msg);

    return { text: msg, json: { error: msg } };
  }

  const lines: string[] = [];
  const header = `PRINT MISSION ZONES: ${mission.name} (inst ${mission.instance_id})`;

  console.groupCollapsed(header);
  lines.push(header);

  const zoneJsons = mission.getZones().map((z) => {
    const zoneHeader = `Zone ${z.zone_name}`;
    console.groupCollapsed(zoneHeader);

    const zoneLines: string[] = [zoneHeader];

    const log = (label: string, val: unknown) => {
      console.log(label, val);
      zoneLines.push(`${label} ${JSON.stringify(val)}`);
    };

    const outerBoundaries = z.getOuterZoneBoundarys().map((b) => ({
      polyId: b.poly_id,
      innerZoneId: b.InnerZone_id,
      outerZoneId: b.OuterZone_id,
      instanceId: b.instance_id,
    }));
    const innerBoundaries = z.getInnerZoneBoundarys().map((b) => ({
      polyId: b.poly_id,
      innerZoneId: b.InnerZone_id,
      outerZoneId: b.OuterZone_id,
      instanceId: b.instance_id,
    }));
    const groupedByPoly = z.getPolysGroupedByPolyId();
    const sampleZone = z.getSampleZone();

    log('instanceId: ', z.instance_id);
    log('outer boundaries: ', outerBoundaries);
    log('inner boundaries: ', innerBoundaries);
    log('grouped by poly', groupedByPoly);

    if (sampleZone) {
      log('sample zone: ', sampleZone);
    }

    console.groupEnd();
    lines.push(indentLines(zoneLines.join('\n'), 1));

    return {
      instance_id: z.instance_id,
      zone_name: z.zone_name,
      zone_type: z.zone_type,
      outer_boundaries: outerBoundaries,
      inner_boundaries: innerBoundaries,
      poly_ids: Object.keys(groupedByPoly).map((k) => parseInt(k, 10)),
      sample_zone_instance_id: sampleZone?.instance_id,
    };
  });

  const sampleZonesHeader = '-- SAMPLE ZONES --';
  console.groupCollapsed(sampleZonesHeader);
  lines.push(sampleZonesHeader);

  const sampleSitesSampleZones = mission
    .getSampleSites()
    .map((site) => site.getSampleZone())
    .filter((szone) => !!szone);

  const zonesSampleZones = mission
    .getZones()
    .map((zone) => zone.getSampleZone())
    .filter((szone) => !!szone);

  const sampleZoneJsons = _.uniq(sampleSitesSampleZones.concat(zonesSampleZones)).map((sampleZone) => {
    const szHeader = `Sample Zone Id: ${sampleZone.instance_id}`;
    console.groupCollapsed(szHeader);

    const szLines: string[] = [szHeader];

    const log = (line: string) => {
      console.log(line);
      szLines.push(line);
    };

    log(`Zone Id: ${sampleZone.Zone_id}`);
    log(`Sample Site Id: ${sampleZone.SampleSite_id}`);
    log(`Sample Id: ${sampleZone.sample_id}`);
    log(`Original Sample Id: ${sampleZone.getSampleSite()?.original_sample_id}`);

    console.groupEnd();
    lines.push(indentLines(szLines.join('\n'), 1));

    return {
      instance_id: sampleZone.instance_id,
      Zone_id: sampleZone.Zone_id,
      SampleSite_id: sampleZone.SampleSite_id,
      sample_id: sampleZone.sample_id,
      original_sample_id: sampleZone.getSampleSite()?.original_sample_id,
    };
  });

  console.groupEnd();
  console.groupEnd();

  const json = {
    mission_name: mission.name,
    mission_instance_id: mission.instance_id,
    zones: zoneJsons,
    sample_zones: sampleZoneJsons,
  };

  return { text: lines.join('\n'), json };
};

export function printJobs(jobs: LocalAtRecord<JobsFieldSet>[], concise = true) {
  console.groupCollapsed('-- PRINT JOBS --');
  jobs.forEach((j) => (concise ? console.log(`Job ${j.get('Name')} - ${j.id} - ${j.get('Sched Date')}`) : printJob(j)));
  console.groupEnd();
}

export function printJob(job: LocalAtRecord<JobsFieldSet>, collapsed: boolean = true) {
  const groupTitle = `Job ${job.get('Name')}`;

  if (collapsed) {
    console.groupCollapsed(groupTitle);
  } else {
    console.group(groupTitle);
  }

  console.log('id:', job.id);
  console.log('Sched date:', job.get('Sched Date'));
  console.log('Robot:', job.get('Robot'));
  console.log('Rig #:', job.get('Rig #'));
  console.log('Client:', job.get('Client'));
  console.log('Grower:', job.get('Grower'));
  console.log('Field Name Clean:', job.get('Field Name Clean'));
  console.log('Lab - Final:', job.get('Lab - Final'));
  console.log('Lab Code:', job.get('Lab Code'));
  console.log('Shift ID:', job.get('Shift'));
  console.log('Operator Name:', job.get('Operator Name'));

  console.groupEnd();
}
